import React, { useEffect, useState } from "react";
import { Bell, X, Info, Tag, AlertTriangle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../contexts/AppContext";
import {
  listNotificationsForUser, getReadNotificationIds, markNotificationRead,
} from "../services/analytics";

const TYPE_ICONS = { info: Info, promo: Tag, warn: AlertTriangle };
const TYPE_COLORS = { info: "var(--primary)", promo: "var(--accent)", warn: "#e0a000" };

export default function NotificationBell() {
  const { user } = useAuth();
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [readIds, setReadIds] = useState(new Set());

  const reload = async () => {
    if (!user) return;
    const [list, reads] = await Promise.all([
      listNotificationsForUser(user.uid),
      getReadNotificationIds(user.uid),
    ]);
    setItems(list);
    setReadIds(reads);
  };

  useEffect(() => { reload(); const id = setInterval(reload, 30000); return () => clearInterval(id); // eslint-disable-next-line
  }, [user]);

  if (!user) return null;

  const unread = items.filter((n) => !readIds.has(n.id)).length;

  const markAll = async () => {
    await Promise.all(items.filter((n) => !readIds.has(n.id)).map((n) => markNotificationRead(user.uid, n.id)));
    reload();
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="metadata flex items-center gap-1 hover:text-[var(--primary)] relative" data-testid="notif-bell">
        <Bell size={14} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-2 bg-[var(--accent)] text-[var(--inverse)] text-[10px] font-mono w-4 h-4 rounded-full flex items-center justify-center" data-testid="notif-badge">{unread}</span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-3 w-80 sm:w-96 bg-[var(--bg)] border border-[var(--border)] shadow-2xl z-50 rounded-md overflow-hidden notif-panel-anim origin-top-right" data-testid="notif-panel">
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-[var(--border)] bg-[var(--surface)]">
              <div className="metadata flex items-center gap-2 text-[var(--text)]"><Bell size={12} /> {t.notif.title}</div>
              {unread > 0 && <button onClick={markAll} className="metadata text-[var(--accent)] hover:underline" data-testid="notif-mark-all">{t.notif.markAllRead}</button>}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 && (
                <div className="p-10 text-center text-[var(--text-muted)] text-sm flex flex-col items-center gap-3">
                  <Bell size={28} className="opacity-30"/>
                  <span>{t.notif.empty}</span>
                </div>
              )}
              {items.map((n, idx) => {
                const Icon = TYPE_ICONS[n.type] || Info;
                const isRead = readIds.has(n.id);
                return (
                  <button
                    key={n.id}
                    onClick={() => markNotificationRead(user.uid, n.id).then(reload)}
                    className={`w-full text-left px-4 py-3.5 border-b border-[var(--border-soft)] last:border-b-0 flex gap-3 hover:bg-[var(--surface)] transition-colors notif-item-anim ${isRead ? "opacity-60" : ""}`}
                    style={{ animationDelay: `${idx * 40}ms` }}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <span
                      className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: `${TYPE_COLORS[n.type] || "var(--primary)"}15` }}
                    >
                      <Icon size={14} style={{ color: TYPE_COLORS[n.type] || "var(--primary)" }} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm leading-snug">{n.title}</div>
                      <div className="text-xs text-[var(--text-2)] mt-1 line-clamp-2 leading-relaxed">{n.body}</div>
                      <div className="metadata text-[var(--text-muted)] mt-1.5 text-[10px]">{n.created_at?.toDate?.().toLocaleString() || ""}</div>
                    </div>
                    {!isRead && <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] mt-2 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
